import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMyWorkspaces } from '@/hooks/use-workspaces';
import { ChevronDown, Check, Briefcase, ShieldCheck } from '@/components/icons';
import { useAuth } from '@/contexts/auth-context';
import { useProperty } from '@/contexts/property-context';

const EMPTY_LIST: never[] = [];

export const WorkspaceSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { data: allMemberships = EMPTY_LIST, isLoading } = useMyWorkspaces();
  const { user } = useAuth();
  const { activeWorkspaceId, switchWorkspace } = useProperty();
  const [isOpen, setIsOpen] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin';
  const memberships = isSuperAdmin
    ? allMemberships
    : allMemberships.filter((m) => ['owner', 'manager', 'auditor'].includes(m.role));

  const activeMembership =
    memberships.find((m) => m.workspace_id === activeWorkspaceId) || memberships[0];

  const handleSelect = (wsId: string) => {
    switchWorkspace(wsId);
    setIsOpen(false);
  };

  if (isLoading || (memberships.length === 0 && !isSuperAdmin)) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-border bg-card hover:bg-secondary/60 text-foreground transition-colors text-xs font-medium cursor-pointer"
      >
        {activeMembership?.logo_data ? (
          <img
            src={activeMembership.logo_data}
            alt={activeMembership.workspace_name}
            className="w-4 h-4 object-contain rounded-xs shrink-0"
          />
        ) : (
          <Briefcase className="w-3.5 h-3.5 text-primary shrink-0" />
        )}
        <span className="max-w-[130px] sm:max-w-[160px] truncate font-semibold">
          {activeMembership ? activeMembership.workspace_name : t('workspace.switcher.select_workspace', 'Select Workspace')}
        </span>
        {activeMembership?.role === 'superadmin' && (
          <span className="px-1 py-0.2 text-[9px] font-bold uppercase rounded bg-purple-500/20 text-purple-400 border border-purple-500/30">
            {t('workspace.switcher.admin_badge', 'Admin')}
          </span>
        )}
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute left-0 mt-1.5 w-64 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-50 p-1.5 text-xs animate-in fade-in zoom-in-95 duration-150">
            <div className="px-2.5 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              {t('workspace.switcher.workspaces_portfolios', 'Workspaces & Portfolios')}
            </div>
            <div className="space-y-0.5 max-h-56 overflow-y-auto">
              {memberships.map((m) => {
                const isSelected = activeMembership && activeMembership.workspace_id === m.workspace_id;
                return (
                  <button
                    key={m.id}
                    onClick={() => handleSelect(m.workspace_id)}
                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left transition-colors cursor-pointer ${
                      isSelected ? 'bg-primary/15 text-primary font-semibold' : 'hover:bg-secondary text-foreground'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {m.logo_data ? (
                        <img
                          src={m.logo_data}
                          alt={m.workspace_name}
                          className="w-4 h-4 object-contain rounded-xs shrink-0"
                        />
                      ) : (
                        <Briefcase className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                      )}
                      <div className="flex flex-col truncate">
                        <span className="truncate">{m.workspace_name}</span>
                        <span className="text-[10px] text-muted-foreground capitalize">
                          {m.role} &bull; {m.workspace_status}
                        </span>
                      </div>
                    </div>
                    {isSelected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-1.5 pt-1.5 border-t border-border flex flex-col gap-1">
              {isSuperAdmin && (
                <a
                  href="/superadmin"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-purple-400 hover:bg-purple-500/15 font-semibold transition-colors"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {t('workspace.switcher.super_admin_mode', 'Super Admin Mode')}
                </a>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
